// Regras de exibição do Monitor de Robôs (sem React, para testar isolado). O farol vem pronto do backend
// (RoboMonitorRegras); aqui só a ordem, os grupos dos indicadores e os textos de tempo.

// tom = cor do tema (success/error/warning/info) ou null (cinza); icone acompanha o rótulo: nunca só a cor
export const FAROIS = {
    PARADO: { tom: "error", icone: "power_off", ordem: 0, grupo: "problema" },
    TRAVADO: { tom: "warning", icone: "hourglass_disabled", ordem: 1, grupo: "problema" },
    ERRO: { tom: "error", icone: "error", ordem: 2, grupo: "problema" },
    ATRASADO: { tom: "warning", icone: "schedule", ordem: 3, grupo: "problema" },
    EM_EXECUCAO: { tom: "info", icone: "sync", ordem: 4, grupo: "execucao" },
    OK: { tom: "success", icone: "check_circle", ordem: 5, grupo: "ok" },
    AGUARDANDO: { tom: null, icone: "hourglass_empty", ordem: 6, grupo: "ok" },
    DESLIGADO: { tom: null, icone: "toggle_off", ordem: 7, grupo: "desligado" },
    INATIVO: { tom: null, icone: "block", ordem: 8, grupo: "desligado" },
};

export const GRUPOS = {
    problema: { rotulo: "Com problema", icone: "report", tom: "error", detalhe: "erro, atraso, travado ou serviço parado" },
    execucao: { rotulo: "Em execução", icone: "sync", tom: "info", detalhe: "rodando agora" },
    ok: { rotulo: "Funcionando", icone: "check_circle", tom: "success", detalhe: "última execução sem erro" },
    desligado: { rotulo: "Desligados", icone: "toggle_off", tom: null, detalhe: "no parâmetro ou fora da agenda" },
};

// Situações do histórico (SituacaoExecucaoRoboEnum)
export const SITUACOES = {
    1: { rotulo: "Em execução", tom: "info", icone: "sync" },
    2: { rotulo: "Sucesso", tom: "success", icone: "check_circle" },
    3: { rotulo: "Sem trabalho", tom: null, icone: "remove_circle_outline" },
    4: { rotulo: "Concluída com erros", tom: "error", icone: "error_outline" },
    5: { rotulo: "Falha", tom: "error", icone: "error" },
    6: { rotulo: "Interrompida", tom: "error", icone: "cancel" },
    7: { rotulo: "Desligado no parâmetro", tom: null, icone: "toggle_off" },
};

export const FILTROS_SITUACAO = [
    { id: "todas", value: "todas", label: "Todas", situacoes: [] },
    { id: "erros", value: "erros", label: "Com erro", situacoes: [4, 5, 6] },
    { id: "sucesso", value: "sucesso", label: "Sucesso", situacoes: [2] },
    { id: "semTrabalho", value: "semTrabalho", label: "Sem trabalho", situacoes: [3] },
    { id: "desligado", value: "desligado", label: "Desligado", situacoes: [7] },
];

export const grupoDoFarol = (farol) => (FAROIS[farol] || FAROIS.AGUARDANDO).grupo;

/** Problemas primeiro (do mais grave), depois em execução, funcionando, desligados; dentro de cada um, pelo nome. */
export const ordenarRobos = (robos) =>
    [...(robos || [])].sort((a, b) => {
        const oa = (FAROIS[a.farol] || FAROIS.AGUARDANDO).ordem;
        const ob = (FAROIS[b.farol] || FAROIS.AGUARDANDO).ordem;
        if (oa !== ob) return oa - ob;
        return String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");
    });

export const contarGrupos = (robos) => {
    const total = { problema: 0, execucao: 0, ok: 0, desligado: 0 };
    (robos || []).forEach((r) => { total[grupoDoFarol(r.farol)] += 1; });
    return total;
};

const semAcento = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Busca por nome, chave (pacote.Classe.método), máquina ou resumo, sem diferenciar acento e maiúscula. */
export const correspondeBusca = (robo, busca) => {
    const termo = semAcento(busca).trim();
    if (!termo) return true;
    return [robo.nome, robo.chave, robo.maquina, robo.ultimoResumo].some((c) => semAcento(c).includes(termo));
};

/** Duração em segundos: "8 s", "3 min 20 s", "2 h 5 min". */
export const formatarDuracao = (segundos) => {
    if (segundos === null || segundos === undefined) return "-";
    const s = Math.max(0, Math.round(segundos));
    if (s < 60) return `${s} s`;
    if (s < 3600) {
        const resto = s % 60;
        return `${Math.floor(s / 60)} min${resto ? ` ${resto} s` : ""}`;
    }
    const min = Math.floor((s % 3600) / 60);
    return `${Math.floor(s / 3600)} h${min ? ` ${min} min` : ""}`;
};

/** "agora há pouco", "há 5 min", "há 2 h", "há 3 dias" (passado) ou "em 10 min" (futuro), a partir do relógio do servidor. */
export const tempoRelativo = (data, agora) => {
    if (!data || !agora) return "-";
    const diff = Math.round((agora - data) / 1000);
    const abs = Math.abs(diff);
    let texto;
    if (abs < 60) return diff >= 0 ? "agora há pouco" : "em instantes";
    if (abs < 3600) texto = `${Math.floor(abs / 60)} min`;
    else if (abs < 86400) texto = `${Math.floor(abs / 3600)} h`;
    else {
        const dias = Math.floor(abs / 86400);
        texto = `${dias} ${dias === 1 ? "dia" : "dias"}`;
    }
    return diff >= 0 ? `há ${texto}` : `em ${texto}`;
};

const doisDigitos = (n) => String(n).padStart(2, "0");

/** "hoje 17:00", "amanhã 07:00", "ontem 20:55" ou "03/10 17:00" (horário local do navegador). */
export const dataCurta = (data, agora) => {
    if (!data) return "-";
    const d = new Date(data);
    const hoje = new Date(agora || Date.now());
    const dia = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const dif = Math.round((dia(d) - dia(hoje)) / 86400000);
    const hora = `${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
    if (dif === 0) return `hoje ${hora}`;
    if (dif === 1) return `amanhã ${hora}`;
    if (dif === -1) return `ontem ${hora}`;
    return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)} ${hora}`;
};

export const dataHora = (data) => {
    if (!data) return "-";
    const d = new Date(data);
    return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)}/${d.getFullYear()} ${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}:${doisDigitos(d.getSeconds())}`;
};

export const formatarTamanho = (bytes) => {
    if (bytes === null || bytes === undefined) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace(".", ",")} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
};

/** YYYY-MM-DD local (o backend lê só a data; toISOString daria o dia anterior à noite). */
export const dataIso = (d) => `${d.getFullYear()}-${doisDigitos(d.getMonth() + 1)}-${doisDigitos(d.getDate())}`;
